import { Controller } from 'stimulus'

/**
 * Manages error spawning and removal for input fields
 *
 * @class ErrorHandlingController
 * @memberof Controllers
 */
export default class extends Controller {
  static targets = [
    'errorList',
    'input'
  ]

  /**
   * @function controllers.ErrorHandlingController.initialize
   * @memberof controllers
   * @description triggers when the controller initializes and adds event listeners
   *
   * @return {undefined} attaches event listeners
   */
  initialize() {
    this.inputTarget.addEventListener('spawnErrors', this._addErrorsToField.bind(this))
    this.inputTarget.addEventListener('removeErrors', this._removeErrorsFromField.bind(this))
  }

  /**
   * @function controllers.ErrorHandlingController.teardown
   * @memberof controllers
   * @description tears down component, removes event listeners
   *
   * @return {undefined} removes event listeners
   */
  teardown() {
    this.inputTarget.removeEventListener('spawnErrors', this._addErrorsToField.bind(this))
    this.inputTarget.removeEventListener('removeErrors', this._removeErrorsFromField.bind(this))
  }


  // Private


  /**
   * @function controllers.ErrorHandlingController._addErrorsToField
   * @memberof controllers
   * @description adds errors to an input field
   * @private
   *
   * @param {Event} event
   *
   * @return {undefined} manipulates the dom
   */
  _addErrorsToField(event) {
    event.detail.forEach(error => {
      if (this._errorAlreadyPresent(error)) {
        return
      } else {
        if (this.inputTarget.tagName === 'SELECT') {
          this.inputTarget.classList.add('form__select--error')
        } else {
          this.inputTarget.classList.add('form__text-field--error')
        }
        const errorElement = document.createElement('li')
        errorElement.innerText = error
        this.errorListTarget.appendChild(errorElement)
      }
    })
    this.errorListTarget.classList.remove('form__errors-list--hidden')
  }

  /**
   * @function controllers.ErrorHandlingController._errorAlreadyPresent
   * @memberof controllers
   * @description checks for presence of error messages
   * @private
   *
   * @param {string} errorMessage
   *
   * @return {boolean} true/false
   */
  _errorAlreadyPresent(errorMessage) {
    return [ ...this.errorListTarget.children ].some(element => {
      return element.innerText.includes(errorMessage)
    })
  }

  /**
   * @function controllers.ErrorHandlingController._removeErrorsFromField
   * @memberof controllers
   * @description clears errors from an input
   * @private
   *
   * @return {undefined} manipulates the dom
   */
  _removeErrorsFromField() {
    while (this.errorListTarget.hasChildNodes()) {
      this.errorListTarget.removeChild(this.errorListTarget.firstChild)
    }

    if (this.inputTarget.tagName === 'SELECT') {
      this.inputTarget.classList.remove('form__select--error')
    } else {
      this.inputTarget.classList.remove('form__text-field--error')
    }
    this.errorListTarget.classList.add('form__errors-list--hidden')
  }
}
